import { AnimatePresence, motion } from "framer-motion";
import ChatItem from "../../../molecules/ChatItem";
import "./index.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import LoadingSpinner from "../../../atoms/LoadingSpinner";

const ChatList = ({
    chats,
    selectedChatId,
    setSelectedChatId,
    onRequestDelete,
    chatRefs,
    loading = false,
    pinnedChatIds,
    mutedChatIds,
    onTogglePin,
    onToggleMute,
    typingIndicators,
    emptyMessage,
}) => {
    const [chatDropdownOpenId, setChatDropdownOpenId] = useState(null);
    // Suppress selection briefly after a context menu / long-press opens, so
    // the click that follows it does not also select the chat. Time-based
    // rather than a boolean flag: a flag goes stale when no follow-up click
    // arrives (desktop right-click, touch long-press) and would eat the next
    // genuine tap.
    const suppressSelectUntilRef = useRef(0);
    const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const closeDropdown = () => setChatDropdownOpenId(null);
        window.addEventListener("click", closeDropdown);
        return () => window.removeEventListener("click", closeDropdown);
    }, []);

    const handleDropdownClose = () => setChatDropdownOpenId(null);

    const typingLookup = useMemo(() => {
        if (!typingIndicators) {
            return {};
        }
        return typingIndicators;
    }, [typingIndicators]);

    const renderEmptyState = () => (
        <div className="chat-list__empty" role="status">
            <svg viewBox="0 0 120 120" aria-hidden="true">
                <defs>
                    <radialGradient id="chatListEmptyGradient" cx="50%" cy="50%" r="65%">
                        <stop offset="0%" stopColor="#71e3ff" stopOpacity="0.8" />
                        <stop offset="60%" stopColor="#5c6bff" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#0b1228" stopOpacity="0" />
                    </radialGradient>
                </defs>
                <circle cx="60" cy="60" r="52" fill="url(#chatListEmptyGradient)" />
                <path
                    fill="rgba(140, 188, 255, 0.35)"
                    d="M36 78a4 4 0 0 1-3.733-5.523l10.5-26.489A6 6 0 0 1 48.32 42h23.36a6 6 0 0 1 5.553 3.988l10.5 26.489A4 4 0 0 1 84 78z"
                />
                <circle cx="50" cy="53" r="3" fill="#9bd7ff" />
                <circle cx="70" cy="53" r="3" fill="#9bd7ff" />
            </svg>
            <p>{emptyMessage || "No conversations to display."}</p>
        </div>
    );

    if (loading) {
        return (
            <div className="chat-list chat-list--loading">
                <div className="chat-list-loading">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (!chats.length) {
        return <div className="chat-list chat-list--empty">{renderEmptyState()}</div>;
    }

    return (
        <div className="chat-list" role="list">
            <AnimatePresence initial={false}>
                {chats.map((chat) => {
                    const key = String(chat.id);
                    const isPinned = pinnedChatIds ? pinnedChatIds.has(key) : false;
                    const isMuted = mutedChatIds ? mutedChatIds.has(key) : false;
                    const isTyping = Boolean(
                        typingLookup?.[key] &&
                        Object.keys(typingLookup[key] || {}).length
                    );
                    return (
                        <motion.div
                            key={chat.id}
                            ref={(el) => {
                                chatRefs.current[chat.id] = el;
                            }}
                            layout
                            initial={{ opacity: 0, y: -12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.28, ease: "easeOut" }}
                        >
                            <ChatItem
                                chat={chat}
                                isSelected={String(selectedChatId) === key}
                                isPinned={isPinned}
                                isMuted={isMuted}
                                isTyping={isTyping}
                                onSelect={() => {
                                    if (Date.now() < suppressSelectUntilRef.current) {
                                        return;
                                    }
                                    setSelectedChatId(chat.id);
                                }}
                                onRightClick={(event) => {
                                    event.preventDefault();
                                    suppressSelectUntilRef.current = Date.now() + 600;
                                    setChatDropdownOpenId(chat.id);
                                    setDropdownCoords({ x: event.clientX, y: event.clientY });
                                }}
                                showDropdown={chatDropdownOpenId === chat.id}
                                dropdownCoords={dropdownCoords}
                                onDropdownClose={handleDropdownClose}
                                onTogglePin={() => {
                                    setChatDropdownOpenId(null);
                                    onTogglePin?.(chat.id);
                                }}
                                onToggleMute={() => {
                                    setChatDropdownOpenId(null);
                                    onToggleMute?.(chat.id);
                                }}
                                onRequestDelete={() => {
                                    setChatDropdownOpenId(null);
                                    onRequestDelete?.(chat.id);
                                }}
                            />
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
};

export default ChatList;
